import React, { useCallback } from 'react';
import classNames from 'classnames';
import { useDropzone } from 'react-dropzone';
import { alpha, Box, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SnackbarStore from '../../stores/observables/SnackbarStore';
import { formatSize, getExtension } from '../../lib/File';

type DropzoneClassKey = 'dragActive' | 'root' | 'withFiles' | 'withoutFiles';

export const useDefaultDropzoneStyles = makeStyles((theme: Theme) => ({
  dragActive: {
    background: alpha(theme.palette.secondary.light, 0.15),
    borderColor: theme.palette.secondary.main,
  },
  root: {
    alignItems: 'center',
    border: `${theme.spacing(0.25)} dashed ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    position: 'relative',
  },
  // Placeholder class. Can be overriden by classes.withFiles
  withFiles: {},
  // Placeholder class. Can be overriden by classes.withoutFiles
  withoutFiles: {},
}));

interface FileWithErrors {
  file: File;
  reason: string;
}

export type RenderInputField = () => React.ReactNode;

export interface DropzoneProps {
  // Callback to check if the file was previously selected
  checkFile?: (file: File) => boolean;
  // All dropzones styles must be provided
  // Use `useDefaultDropzoneStyles` for basic styles
  classes: Record<DropzoneClassKey, string>;
  disabled?: boolean;
  hasFiles?: boolean;
  maxFilesize?: number;
  render: (renderInputField: RenderInputField) => React.ReactNode;
  setFiles: (files: File[]) => void;
  supportedFormatMimes?: string[];
  unsupportedFormatExtensions?: string[];
  unsupportedFormatMimes?: string[];
}

const Dropzone = React.forwardRef<HTMLInputElement, DropzoneProps>(
  (props, ref) => {
    const {
      checkFile = () => true,
      classes,
      disabled = false,
      hasFiles = false,
      maxFilesize,
      render,
      setFiles,
      supportedFormatMimes,
      unsupportedFormatExtensions,
      unsupportedFormatMimes,
    } = props;

    const onDrop = useCallback(
      (selectedFiles: File[], _, event) => {
        const filesWithErrors: FileWithErrors[] = [];

        // Apply format and custom checks
        // Compose a list of files with errors
        const filesToAdd = selectedFiles.filter((file) => {
          // Validate if the format matches the supported formats
          const isValidSupported =
            !supportedFormatMimes || supportedFormatMimes.includes(file.type);
          if (!isValidSupported) {
            filesWithErrors.push({
              file,
              reason: 'Sorry, this file type is not allowed',
            });
            return false;
          }
          // Validate if the format doesn't match any unsupported format or extension doesn't match any unsupported format
          const isUnsupported =
            (unsupportedFormatMimes &&
              unsupportedFormatMimes.includes(file.type)) ||
            (unsupportedFormatExtensions &&
              unsupportedFormatExtensions.includes(getExtension(file)));

          if (isUnsupported) {
            filesWithErrors.push({
              file,
              reason: 'Sorry, this file type is not allowed',
            });
            return false;
          }
          // Validate max file size
          if (!!maxFilesize && file.size > maxFilesize) {
            filesWithErrors.push({
              file,
              reason: `File cannot be larger than ${formatSize(maxFilesize)}`,
            });
            return false;
          }
          // Validate if the file was previously selected
          const validCheck = checkFile(file);
          if (!validCheck) {
            filesWithErrors.push({
              file,
              reason: 'Duplicate files were not attached',
            });
            return false;
          }
          // Allow file to be selected
          return true;
        });

        // Delegate files with errors handling
        if (filesWithErrors.length > 0) {
          // We agreed on showing only the first file error
          // @todo improve messaging for multiple files with errors
          SnackbarStore.show(filesWithErrors[0].reason);
        }

        // Transform files into list entries
        setFiles(filesToAdd);
        // The input must be cleaned in order to force
        // firing onChange for the same file selection
        // eslint-disable-next-line no-param-reassign
        event.target.value = '';
      },
      [
        setFiles,
        supportedFormatMimes,
        unsupportedFormatMimes,
        unsupportedFormatExtensions,
        maxFilesize,
        checkFile,
      ]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      onDrop,
      disabled,
    });

    const renderInputField: RenderInputField = () => (
      <input
        {...getInputProps({
          accept: supportedFormatMimes && supportedFormatMimes.join(','),
          //  disabled: isDisabled,
          multiple: true,
          ref,
        })}
      />
    );

    return (
      <Box
        className={classNames(classes.root, {
          [classes.dragActive]: isDragActive,
          [classes.withFiles]: hasFiles,
          [classes.withoutFiles]: !hasFiles,
        })}
        {...getRootProps()}
        data-testid="drag-drop-area"
      >
        {render(renderInputField)}
      </Box>
    );
  }
);

export default Dropzone;
